import { PlusOutlined } from '@ant-design/icons';
import type { CreatePaymentInput, PaymentMethod } from '@financiar/shared';
import { isNegativeAmount, subtractMoney } from '@financiar/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, DatePicker, Form, Input, Modal, Popconfirm, Select, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { getApiError } from '../../api/client';
import type { InvoiceDetail, InvoicePayment } from '../../api/types';
import { applyServerErrors, undefinedIfEmpty } from '../../lib/forms';
import { DateText, formatMoney, MoneyInput, MoneyText } from '../../lib/format';
import { createPayment, deletePayment } from './api';

const { Text } = Typography;

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  BANK: 'Transfer bancar',
  CASH: 'Numerar',
  CARD: 'Card',
  COMPENSATION: 'Compensare',
  OTHER: 'Altă metodă',
};

const PAYMENT_METHOD_OPTIONS = (Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((method) => ({
  value: method,
  label: PAYMENT_METHOD_LABELS[method],
}));

interface PaymentFormValues {
  paidOn: Dayjs;
  amount: string;
  method: PaymentMethod;
  reference?: string;
}

/**
 * The invoice view's "Încasări" section. Payments exist only against an
 * ISSUED invoice (`PaymentsService.assertPayable`) — a draft is not a
 * receivable yet and a cancelled one stopped being one — so the whole card,
 * including deletion, is rendered only in that state.
 */
export function PaymentsCard({
  invoice,
  autoOpen,
  onAutoOpenHandled,
}: {
  invoice: InvoiceDetail;
  /** Set when arriving from the list's "Încasare" action, which opens the
   * modal straight away. */
  autoOpen?: boolean;
  onAutoOpenHandled?: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<PaymentFormValues>();
  const [open, setOpen] = useState(false);

  const rest = subtractMoney(invoice.totalAmount, invoice.paidAmount);

  function openModal() {
    setOpen(true);
    form.setFieldsValue({ paidOn: dayjs(), amount: rest, method: 'BANK', reference: undefined });
  }

  useEffect(() => {
    if (!autoOpen) return;
    openModal();
    onAutoOpenHandled?.();
    // Intentionally keyed on `autoOpen` alone: this is a one-shot reaction to
    // arriving with ?incasare=1, not a subscription to the form/invoice.
  }, [autoOpen]);

  function refresh(updated: InvoiceDetail) {
    queryClient.setQueryData(['invoices', 'detail', updated.id], updated);
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  }

  const createMutation = useMutation({
    mutationFn: (values: PaymentFormValues) =>
      createPayment(invoice.id, {
        paidOn: values.paidOn.format('YYYY-MM-DD'),
        amount: values.amount,
        method: values.method,
        reference: undefinedIfEmpty(values.reference),
      } as CreatePaymentInput),
    onSuccess: (updated) => {
      message.success('Încasarea a fost înregistrată.');
      refresh(updated);
      setOpen(false);
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Încasarea nu a putut fi înregistrată.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (paymentId: string) => deletePayment(invoice.id, paymentId),
    onSuccess: (updated) => {
      message.success('Încasarea a fost ștearsă.');
      refresh(updated);
    },
    onError: (error) => message.error(getApiError(error)?.message ?? 'Încasarea nu a putut fi ștearsă.'),
  });

  const columns: ColumnsType<InvoicePayment> = [
    { title: 'Dată', key: 'paidOn', width: 120, render: (_, row) => <DateText value={row.paidOn} /> },
    {
      title: 'Sumă',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_, row) => (
        <MoneyText
          value={row.amount}
          currency={invoice.currency}
          className={isNegativeAmount(row.amount) ? 'money-negative' : undefined}
        />
      ),
    },
    { title: 'Metodă', key: 'method', width: 160, render: (_, row) => PAYMENT_METHOD_LABELS[row.method] ?? row.method },
    { title: 'Referință', key: 'reference', render: (_, row) => row.reference ?? '—' },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Popconfirm
          title="Ștergi încasarea?"
          description="Restul de încasat se recalculează."
          okText="Șterge"
          cancelText="Renunță"
          onConfirm={() => deleteMutation.mutate(row.id)}
        >
          <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === row.id}>
            Șterge
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title="Încasări"
      size="small"
      style={{ marginTop: 16 }}
      extra={
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openModal}>
          Adaugă încasare
        </Button>
      }
    >
      <Statistic
        title="Rest de încasat"
        value={formatMoney(rest, invoice.currency)}
        valueStyle={{ color: isNegativeAmount(rest) ? '#cf1322' : undefined }}
        style={{ marginBottom: 12 }}
      />
      <Table<InvoicePayment>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={invoice.payments}
        locale={{ emptyText: <Text type="secondary">Nicio încasare înregistrată.</Text> }}
      />

      <Modal
        title="Adaugă încasare"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okText="Înregistrează"
        cancelText="Renunță"
        confirmLoading={createMutation.isPending}
        // `forceRender`, NOT `destroyOnHidden`: `openModal` prefills the
        // amount with the outstanding balance BEFORE the modal is shown, and
        // `form.setFieldsValue` on a Form that hasn't mounted yet is exactly
        // the "Instance created by useForm is not connected to any Form
        // element" case. Rendering the body up front keeps the instance
        // connected; every field is re-set on each open anyway, so nothing
        // leaks between openings.
        forceRender
      >
        <Form<PaymentFormValues> form={form} layout="vertical" onFinish={(values) => createMutation.mutate(values)}>
          <Form.Item name="amount" label="Sumă" rules={[{ required: true, message: 'Suma este obligatorie.' }]}>
            <MoneyInput />
          </Form.Item>
          <Form.Item name="paidOn" label="Dată" rules={[{ required: true, message: 'Data este obligatorie.' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="method" label="Metodă" rules={[{ required: true }]}>
            <Select options={PAYMENT_METHOD_OPTIONS} />
          </Form.Item>
          <Form.Item name="reference" label="Referință">
            <Input placeholder="Nr. extras, OP, chitanță…" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
